/**
 * Contains generalized functionality for managing the Desktop (at the FSBL level rather
 * than at the fin/system level).
 */
import { RouterClient } from "../clients/routerClient";
import { RestartParams } from "../services/systemManager/shutDownManager";
import SystemManagerClient from "../common/systemManagerClient";
import { WindowIdentifier } from "../services/window/types";
import { getMyWindowIdentifier } from "./util";
import { StandardErrorCallback, StandardPromise } from "../types";

/**
 * Shutdown the SmartDesktop (Finsemble).
 */
export const shutdownApplication = () => {
	RouterClient.transmit("Application.shutdown", {});
};

/**
 * Restart the SmartDesktop (Finsemble).
 * @param [params]
 * @param [params.forceRestart]=false Whether to give components a chance to shutdown. ForceRestart is used during the reset sequence.
 */
export const restartApplication = (params?: RestartParams) => {
	RouterClient.transmit("Application.restart", params || {});
};

/**
 * Brings a windows to front. If no windowList, groupName or componentType is specified, brings all windows to front.
 *
 * ```javascript
 * FSBL.Clients.LauncherClient.bringWindowsToFront({ windowList: ['AdvancedChart-123-123', 'Symphony-Chat-234-234']}, function(err, response) {
 *    // do something
 * })
 * ```
 * @param params
 * @param params.windowList Optional. An array An array of window names or window identifiers. Not to be used with componentType.
 * @param params.groupName Optional. The name of a window group to bring to front.
 * @param params.componentType Optional. The componentType to bring to front. Not to be used with windowList.
 */
export const bringWindowsToFront = async (
	params: {
		windowList?: string[] | WindowIdentifier[];
		groupName?: string;
		componentType?: string;
	} = {},
	cb: Function = Function.prototype
) => {
	if (params.windowList && !Array.isArray(params.windowList)) {
		params.windowList = [params.windowList];
	}

	// Changed to query to allow for async bring to front and to do something when all windows have been brought to front
	await SystemManagerClient.waitForStartup("windowService");
	RouterClient.query("LauncherService.bringWindowsToFront", params, (err, response) => {
		cb(err, response);
	});
	return Promise.resolve();
};

/**
 * Minimize windows. If no windowList or groupName is specified, all windows will be minimized.
 * @param params
 * @param params.windowList Optional. An array of window names or window identifiers. Not to be used with componentType.
 * @param params.groupName Optional. The name of a window group to minimize.
 * @param params.componentType Optional. The component type of windows to Minimize. Not to be used with windowList.
 */
export const minimizeWindows = async (
	params: {
		windowList?: string[] | WindowIdentifier[];
		groupName?: string;
		componentType?: string;
	},
	cb?: Function
) => {
	if (params.windowList && !Array.isArray(params.windowList)) {
		params.windowList = [params.windowList];
	}
	await SystemManagerClient.waitForStartup("windowService");
	RouterClient.transmit("LauncherService.minimizeWindows", params);
	cb?.();
};

/**
 * This method is an experimental feature in Finsemble Labs. Calling this method automatically arranges all windows on the user's screen into a grid-like pattern.
 *
 * @param params Parameters Unused
 * @param cb The callback to be invoked after the method completes successfully.
 */
export const autoArrange = async (params: any, cb?: StandardErrorCallback<void>): StandardPromise<void> => {
	params = params || {};
	await SystemManagerClient.waitForStartup("windowService");
	const myWindowIdentifier = await getMyWindowIdentifier();
	const { err } = await RouterClient.query("DockingService.autoArrange", {
		windowIdentifier: myWindowIdentifier,
	});
	cb?.(err);
	return { err };
};

/**
 * Minimizes all windows.
 *
 * ```javascript
 * FSBL.Clients.WorkspaceClient.minimizeAll();
 * FSBL.Clients.WorkspaceClient.minimizeAll({monitor: "all"}, function(err){
 * 	// do something
 * });
 * ```
 * @param params
 * @param params.monitor Same options as <a href="/docs/smart-desktop/windows-and-workspaces/API-LauncherClient#showWindow">LauncherClient.showWindow</a> except that "all" will work for all monitors. Defaults to all.
 * @param params.windowIdentifier The Finsemble identifier structure for the window triggering the request.
 * @param cb The callback to be invoked after the method completes successfully.
 */
export const minimizeAll = async (
	params?: {
		monitor: string;
		windowIdentifier?: any;
	},
	cb?: StandardErrorCallback<void>
): StandardPromise<void> => {
	await SystemManagerClient.waitForStartup("windowService");
	const myWindowIdentifier = await getMyWindowIdentifier();
	let completeParams = params ?? { monitor: "all" };
	completeParams.windowIdentifier = completeParams.windowIdentifier ?? myWindowIdentifier;
	const { err } = await RouterClient.query("WorkspaceService.minimizeAll", completeParams);
	cb?.(err);
	return { err };
};

/**
 * Brings all windows to the front.
 *
 * ```javascript
 * FSBL.Clients.WorkspaceClient.bringWindowsToFront();
 * FSBL.Clients.WorkspaceClient.bringWindowsToFront({monitor: "all"}, function(err){
 * 	// do something
 * });
 * ```
 * @param params
 * @param params.monitor Same options as <a href="/docs/smart-desktop/windows-and-workspaces/API-LauncherClient#showWindow">LauncherClient.showWindow</a> except that "all" will work for all monitors. Defaults to the monitor for the current window.
 * @param params.windowIdentifier The Finsemble identifier for the target window. If not provided, defaults to the current window.
 * @param cb The callback to be invoked after the method completes successfully.
 */
export const bringAllWindowsToFront = async (
	params?: {
		monitor: string;
		windowIdentifier?: any;
	},
	cb?: StandardErrorCallback<void>
): StandardPromise<void> => {
	await SystemManagerClient.waitForStartup("windowService");
	const myWindowIdentifier = await getMyWindowIdentifier();
	const completeParams = params ?? { monitor: "all" };
	completeParams.windowIdentifier = completeParams.windowIdentifier ?? myWindowIdentifier;
	const { err } = await RouterClient.query("WorkspaceService.bringWindowsToFront", completeParams);
	cb?.(err);
	return { err };
};
